import { Task, FullProject, TaskHistoryEntry } from '../../types';
import { diffDays, addDays, getTaskDateBucket } from '../../utils/dateUtils';

/**
 * Deterministic fractional offset (0 < fraction < 1) derived from a project ID
 * so newly created or reordered tasks never receive integer `sortOrder` values.
 */
export function getProjectSortOrderFraction(projectId?: string): number {
  if (!projectId) return 0.1234;
  const raw =
    Array.from(String(projectId)).reduce((sum, char) => sum + char.charCodeAt(0), 0) / 10000;
  const frac = raw - Math.floor(raw);
  return frac > 0.00001 ? Number(raw.toFixed(4)) : 0.1234;
}

/**
 * Guarantees that any `sortOrder` number is non-integer by adding the project's
 * deterministic fractional offset when `value` is an integer or non-finite.
 */
export function ensureFractionalSortOrder(value: number, projectId?: string): number {
  const fraction = getProjectSortOrderFraction(projectId);
  if (!Number.isFinite(value)) return 1000 + fraction;
  return Number.isInteger(value) ? value + fraction : value;
}

/**
 * Ensures all tasks in `p` have non-colliding fractional `sortOrder` values (Copy-on-Write).
 * Returns `true` if existing tasks had to be renumbered due to collisions or legacy integers.
 */
export function ensureProjectMigratedToSortOrder(
  p: FullProject,
  markProjectDirty: (id: string) => void
): boolean {
  const hasMissing = p.tasks.some((t) => typeof t.sortOrder !== 'number');
  const sortOrders = p.tasks
    .map((t) => t.sortOrder)
    .filter((s) => typeof s === 'number') as number[];
  const hasCollisions = new Set(sortOrders).size !== sortOrders.length;
  const hasLegacyIntegers = p.tasks.some(
    (t) => typeof t.sortOrder === 'number' && Number.isInteger(t.sortOrder)
  );

  if (!hasMissing && !hasCollisions && !hasLegacyIntegers) return false;

  p.tasks = p.tasks.map((t) => ({ ...t }));

  const itemOrder = p.metadata?.itemOrder || {};
  const orderedIds: string[] = [];
  Object.keys(itemOrder).forEach((bucketId) => {
    if (Array.isArray(itemOrder[bucketId])) {
      orderedIds.push(...itemOrder[bucketId]);
    }
  });
  const uniqueOrderedIds = Array.from(new Set(orderedIds));
  const projectFraction = getProjectSortOrderFraction(p.id);

  let didRenumberAll = false;
  if (hasCollisions || hasLegacyIntegers) {
    didRenumberAll = true;
    p.tasks.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return (a.sortOrder || 0) - (b.sortOrder || 0);
      return (a.name || '').localeCompare(b.name || '');
    });
    p.tasks.forEach((task, i) => {
      task.sortOrder = (i + 1) * 1000 + projectFraction;
    });
  } else {
    p.tasks.forEach((task) => {
      if (typeof task.sortOrder !== 'number') {
        const orderIdx = uniqueOrderedIds.indexOf(String(task.id));
        if (orderIdx !== -1) {
          task.sortOrder = (orderIdx + 1) * 1000 + projectFraction;
        } else {
          task.sortOrder = 1000000 + p.tasks.indexOf(task) * 10 + projectFraction;
        }
      }
    });
  }

  if (p.metadata) {
    delete p.metadata.itemOrder;
  }
  markProjectDirty(p.id);
  return didRenumberAll;
}

/**
 * Applies bucket transition mutations (status, completed, archived, start/end dates, timer stop)
 * to `task` in place.
 */
export function applyBucketTransitionToTask(params: {
  task: Task;
  targetBucketId: string;
  mode: 'status' | 'date' | 'custom';
  currentDateBucket: string;
  freshToday: string;
  targetProject: FullProject;
}): void {
  const { task, targetBucketId, mode, currentDateBucket, freshToday, targetProject } = params;

  if (targetBucketId === 'archived') {
    task.archived = true;
  } else {
    if (task.archived) {
      task.archived = false;
    }
    if (mode === 'date') {
      if (targetBucketId === 'completed') {
        task.completed = true;
        task.status = 'completed';
      } else if (targetBucketId === 'hold') {
        task.completed = false;
        task.status = 'hold';
      } else if (currentDateBucket === targetBucketId) {
        task.completed = false;
        if (task.status === 'completed' || task.status === 'hold') {
          task.status = 'open';
        }
      } else {
        task.completed = false;
        if (task.status !== 'pending') {
          task.status = 'open';
        }
        const duration = task.start && task.end ? diffDays(task.start, task.end) : 0;

        if (targetBucketId === 'today') {
          task.start = freshToday;
          task.end = addDays(task.start, duration);
        } else if (targetBucketId === 'week') {
          task.start = addDays(freshToday, 1);
          task.end = addDays(task.start, duration);
        } else if (targetBucketId === 'month') {
          task.start = addDays(freshToday, 8);
          task.end = addDays(task.start, duration);
        } else if (targetBucketId === 'longterm') {
          task.start = addDays(freshToday, 31);
          task.end = addDays(task.start, duration);
        } else if (targetBucketId === 'overdue') {
          task.end = addDays(freshToday, -1);
          task.start = addDays(task.end, -duration);
        } else {
          task.start = freshToday;
          task.end = addDays(task.start, duration);
        }
      }
    } else {
      if (targetBucketId === 'todo') {
        task.status = 'open';
        task.completed = false;
      } else if (targetBucketId === 'completed') {
        task.status = 'completed';
        task.completed = true;
      } else if (targetBucketId === 'hold') {
        task.status = 'hold';
        task.completed = false;
      } else {
        const customCol = targetProject.metadata?.customColumns?.find(
          (c) => c.id === targetBucketId
        );
        task.completed = customCol?.category === 'completed';
        if (customCol) {
          task.status = targetBucketId;
        } else if (task.status === 'hold') {
          task.status = 'open';
        } else {
          task.status = targetBucketId;
        }
      }
    }
  }

  if (
    (task.completed || task.status === 'completed' || task.status === 'hold' || task.archived) &&
    task.timerStartedAt
  ) {
    const elapsed = Date.now() - task.timerStartedAt;
    task.trackedTime = (task.trackedTime || 0) + elapsed;
    task.timerStartedAt = null;
  }
}

/**
 * Computes the non-integer `sortOrder` for a task dropped into `targetBucketId`.
 */
export function computeTargetSortOrder(params: {
  taskId: string | number;
  targetProject: FullProject;
  targetBucketId: string;
  overId?: string | number;
  position: 'before' | 'after' | 'end';
  mode: 'status' | 'date' | 'custom';
  freshToday: string;
  explicitPrevSort?: number;
  explicitNextSort?: number;
}): number {
  const {
    taskId,
    targetProject,
    targetBucketId,
    overId,
    position,
    mode,
    freshToday,
    explicitPrevSort,
    explicitNextSort,
  } = params;

  const targetProjectFraction =
    Array.from(targetProject.id).reduce((sum, char) => sum + char.charCodeAt(0), 0) / 10000 ||
    0.1234;
  let newSortOrder: number = 1000 + targetProjectFraction;

  if (explicitPrevSort !== undefined || explicitNextSort !== undefined) {
    if (explicitPrevSort !== undefined && explicitNextSort !== undefined) {
      if (explicitPrevSort === explicitNextSort) {
        newSortOrder = explicitPrevSort + 0.001;
      } else {
        newSortOrder = (explicitPrevSort + explicitNextSort) / 2;
      }
    } else if (explicitPrevSort !== undefined) {
      newSortOrder = explicitPrevSort + 1000;
    } else if (explicitNextSort !== undefined) {
      newSortOrder = explicitNextSort - 1000;
    }
  } else {
    const bucketTasks = (targetProject.tasks || [])
      .filter((t) => String(t.id) !== String(taskId))
      .filter((t) =>
        mode === 'date'
          ? getTaskDateBucket(t, freshToday) === targetBucketId
          : (t.status || 'open') === targetBucketId
      )
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    if (bucketTasks.length === 0) {
      newSortOrder = 1000 + targetProjectFraction;
    } else if (position === 'end' || !overId) {
      newSortOrder = (bucketTasks[bucketTasks.length - 1].sortOrder || 0) + 1000;
    } else {
      const idx = bucketTasks.findIndex((t) => String(t.id) === String(overId));
      if (idx === -1) {
        newSortOrder = (bucketTasks[bucketTasks.length - 1].sortOrder || 0) + 1000;
      } else {
        const insertIdx = position === 'before' ? idx : idx + 1;
        if (insertIdx <= 0) {
          newSortOrder = (bucketTasks[0].sortOrder || 0) - 1000;
        } else if (insertIdx >= bucketTasks.length) {
          newSortOrder = (bucketTasks[bucketTasks.length - 1].sortOrder || 0) + 1000;
        } else {
          const s1 = bucketTasks[insertIdx - 1].sortOrder || 0;
          const s2 = bucketTasks[insertIdx].sortOrder || 0;
          newSortOrder = (s1 + s2) / 2;
        }
      }
    }
  }

  if (Number.isInteger(newSortOrder)) {
    newSortOrder += targetProjectFraction;
  }
  return newSortOrder;
}

/**
 * Builds history entries for a drag-and-drop reorder transition.
 */
export function buildReorderHistoryEntries(params: {
  task: Task;
  prevProjectId: string;
  targetProject: FullProject;
  prevStatus?: string;
  prevStart?: string;
  prevEnd?: string;
  userEmail?: string;
}): TaskHistoryEntry[] {
  const { task, prevProjectId, targetProject, prevStatus, prevStart, prevEnd, userEmail } = params;
  const historyEntries: TaskHistoryEntry[] = [];
  const userStr = userEmail || 'System';

  if (prevProjectId !== targetProject.id) {
    const targetProjName = targetProject.name || targetProject.id;
    historyEntries.push({
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      user: userStr,
      field: 'projectId',
      oldValue: prevProjectId,
      newValue: targetProject.id,
      message: `Aufgabe in das Projekt "${targetProjName}" verschoben`,
    });
  }

  if (prevStatus !== task.status) {
    historyEntries.push({
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      user: userStr,
      field: 'status',
      oldValue: prevStatus ?? null,
      newValue: task.status ?? null,
      message: `Status geändert von "${prevStatus || 'offen'}" zu "${task.status || 'offen'}"`,
    });
  }

  if (prevStart !== task.start) {
    historyEntries.push({
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      user: userStr,
      field: 'start',
      oldValue: prevStart ?? null,
      newValue: task.start ?? null,
      message: `Startdatum geändert von "${prevStart || 'keins'}" zu "${task.start || 'keins'}"`,
    });
  }

  if (prevEnd !== task.end) {
    historyEntries.push({
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      user: userStr,
      field: 'end',
      oldValue: prevEnd ?? null,
      newValue: task.end ?? null,
      message: `Enddatum geändert von "${prevEnd || 'keins'}" zu "${task.end || 'keins'}"`,
    });
  }

  return historyEntries;
}
